import type { ModuleId } from "../domain/chat.js";
import type { ParsedTurn, TurnDiagnostic } from "../game/turn-protocol.js";
import type { ViewSpec } from "../game/view-spec.js";

/**
 * 게임 모듈 포트 — 결정 #25(GameModule 추상화). 모든 게임(롤플레잉·텍스트
 * 어드벤처·스탯 RPG·비주얼노벨·소셜 추리 …)이 공유하는 계약이다.
 *
 * core는 "계약 + 플랫폼 중립 훅"만 정의한다(결정 #10). 프롬프트 조립의 무거운
 * 부분(캐릭터/페르소나/로어북 해석)은 web 오케스트레이션이 담당하고, 모듈은
 * 거기에 끼어드는 가벼운 순수 훅(턴 프로토콜 지시문·상태 반영·뷰 선언)을 제공한다.
 *
 * 모든 런타임 훅은 옵셔널이다: 훅이 없는 모듈(예: 롤플레잉)은 기존 자유대화
 * 동작을 그대로 쓴다(결정 #28 가산성). 구조화 게임(텍스트 어드벤처 등)이 훅을 구현.
 */

/** 모듈 구현 방식(결정 #24). */
export type GameModuleKind =
  | "native" // 우리가 TS/React로 직접 작성한 번들 모듈
  | "data"; // 매니페스트 + 샌드박스 스크립트로 정의된 데이터형 모듈(#29)

/**
 * 게임 모듈의 정적 메타데이터. 모듈 목록 UI·세션→모듈 해석에 쓰인다. 데이터형
 * 모듈(#29)은 카드 매니페스트가 이 모양을 만들어 등록한다.
 */
export interface GameModuleManifest {
  /** 안정적 식별자(인스턴스 UUID가 아니라 이름). 예: "roleplay". */
  readonly id: ModuleId;
  /** 사용자 표시 이름. */
  readonly title: string;
  /** 로컬라이징을 위한 i18n 번역 키. */
  readonly titleKey?: string;
  /** 한 줄 설명(모듈 선택 화면용). */
  readonly description?: string;
  /** 로컬라이징을 위한 i18n 번역 키. */
  readonly descriptionKey?: string;
  readonly kind: GameModuleKind;
  /** 아이콘 힌트(이모지/아이콘 키). UI가 해석. */
  readonly icon?: string;
}

/** 한 턴의 입력(결정 #26). 모듈의 applyTurn이 받는다. */
export interface GameTurnInput {
  /** 현재 모듈 상태(Chat.moduleState). 없으면 빈 객체. */
  state: Record<string, unknown>;
  /** 이번 턴의 파싱된 AI 출력(#26). */
  parsed: ParsedTurn;
  /** 재현 가능한 시드 RNG(#4). 주사위 등에 사용. */
  random: () => number;
}

/** applyTurn 결과(결정 #26/#28). */
export interface GameTurnOutcome {
  /** 다음에 영속할 모듈 상태. */
  state: Record<string, unknown>;
  /** 게임 종료 여부(승패/엔딩). UI가 후속 입력을 막는 데 쓴다. */
  ended?: boolean;
  /** 종료 사유/엔딩 라벨(있으면 표시). */
  endingLabel?: string;
  /** 엔진이 무시한 불완전/비허용 action 진단. */
  diagnostics?: TurnDiagnostic[];
}

/**
 * 세션 시작 시 플레이어가 고른 setup 선택값(갭3). 필드 키 → 선택값. 데이터형 게임의
 * `setup` 선언에 대응한다(예: { player_gender: "M", player_role: "stu" }). 모듈의
 * initialState가 이 값으로 초기 상태를 파생한다(예: KWU의 성별×소속 분기).
 */
export type SessionSetup = Record<string, string>;

/**
 * 한 게임 모듈. 매니페스트는 필수, 런타임 훅은 옵셔널(결정 #25/#26/#27).
 */
export interface GameModule {
  readonly manifest: GameModuleManifest;

  /**
   * 새 세션이 시작될 때의 초기 모듈 상태(결정 #28). 세션 생성 시 한 번 호출되어
   * Chat.moduleState의 시드가 된다. 미구현 모듈(롤플레잉 등 자유대화)은 상태가
   * 없으므로 호출되지 않는다(undefined ⇒ 빈 상태).
   *
   * setup(갭3): 세션 시작 시 플레이어가 고른 선택값. 모듈이 이를 초기 상태에 병합하고
   * (선언된 키만, #17) initRules로 파생 상태를 산출한다. 인자 없이 호출하면 기본 초기
   * 상태(하위호환). 네이티브 모듈은 setup을 무시해도 된다.
   */
  initialState?(setup?: SessionSetup, t?: (k: string, ...args: any[]) => string): Record<string, unknown>;

  /**
   * 이 게임의 태그 문법·규칙을 모델에 지시하는 시스템 프롬프트 조각(결정 #26).
   * 프롬프트 조립 시 시스템 블록에 더해진다. 현재 상태를 받아 동적으로 구성 가능
   * (예: "현재 HP=40, 인벤토리=[검]"). 자유대화 모듈은 미구현.
   */
  protocolInstructions?(state: Record<string, unknown>, t?: (k: string, ...args: any[]) => string): string;

  /**
   * 파싱된 턴의 구조화 출력을 모듈 상태에 반영한다(결정 #26/#28). 순수 함수 —
   * 새 상태를 반환하고, 호출부가 세션에 저장한다. 자유대화 모듈은 미구현.
   */
  applyTurn?(input: GameTurnInput, t?: (k: string, ...args: any[]) => string): GameTurnOutcome;

  /**
   * 현재 상태에서 무엇을 렌더할지 선언한다(결정 #27). web 렌더러가 ViewSpec의
   * 위젯을 그린다. 자유대화 모듈은 미구현(메시지 목록만).
   */
  viewSpec?(state: Record<string, unknown>, t?: (k: string, ...args: any[]) => string): ViewSpec;
}

/**
 * 설치된 게임 모듈 레지스트리(결정 #25). 플랫폼 중립 순수 컨테이너 — web이
 * 부팅 시 모듈을 등록하고, 세션은 `moduleId`로 자기 모듈을 찾는다.
 */
export class GameModuleRegistry {
  private readonly modules = new Map<ModuleId, GameModule>();

  /** 모듈을 등록한다. 같은 id 재등록은 덮어쓴다(HMR/재부팅 안전). */
  register(module: GameModule): void {
    this.modules.set(module.manifest.id, module);
  }

  /** 모듈을 제거한다(데이터형 카드 비활성화/삭제 시, 결정 #29). 없으면 false. */
  unregister(id: ModuleId): boolean {
    return this.modules.delete(id);
  }

  /** id로 모듈을 찾는다(없으면 undefined). */
  get(id: ModuleId): GameModule | undefined {
    return this.modules.get(id);
  }

  /** 등록된 모든 모듈(목록 UI용). 등록 순서 보존. */
  list(): GameModule[] {
    return [...this.modules.values()];
  }

  /** 해당 모듈이 등록돼 있는지. */
  has(id: ModuleId): boolean {
    return this.modules.has(id);
  }
}
